import {generateLocalId} from '../../../yMaps/referencesYMap';

/**
 * Build a citation object compatible with handleAddSuggestedReference from
 * supporting evidence metadata (added reference or corpus discovery entry).
 *
 * @param {Object} params
 * @param {Object|null} params.reference   Added reference from Y.Map, if any
 * @param {Object|null} params.corpusEntry supportingEvidenceTexts entry
 * @param {string|null} params.referenceId Evidence referenceId
 * @returns {Object|null}
 */
export function buildCitationFromEvidence({reference, corpusEntry, referenceId}) {
    if (reference) {
        const citation = {
            localId: reference.localId || reference.firestoreId || null,
            docId: reference.docId,
            segmentId: reference.segmentId ?? null,
            index: reference.index ?? reference.segmentIndex ?? null,
            offset: reference.offset ?? 0,
            content: reference.content || '',
            docTitle: reference.docTitle || reference.title || reference.name || 'Reference',
            docSource: reference.docSource || null,
            docUrl: reference.docUrl || null,
            sourceScore: reference.sourceScore ?? null,
            start: reference.start || null,
            end: reference.end || null,
            tags: reference.tags || null,
        };
        citation.localId = citation.localId || generateLocalId(citation);
        return citation.localId || citation.docId != null ? citation : null;
    }

    if (!corpusEntry) return null;

    const docId = corpusEntry.docId ?? corpusEntry.dataItemReferenceId ?? corpusEntry.canonicalFileId ?? null;
    if (docId == null) return null;

    const index = corpusEntry.segmentIndex ?? corpusEntry.segmentId ?? 0;
    const offset = 0;
    const start = {segmentIndex: index, segmentOffset: offset};
    const end = {segmentIndex: index, segmentOffset: offset};

    const citation = {
        docId,
        segmentId: corpusEntry.segmentId ?? corpusEntry.dataItemSegmentId ?? null,
        index,
        offset,
        content: corpusEntry.text || '',
        docTitle: corpusEntry.docTitle || 'Corpus passage',
        docSource: null,
        docUrl: null,
        start,
        end,
    };

    citation.localId = generateLocalId(citation) || referenceId || null;
    return citation.localId ? citation : null;
}

/**
 * @param {Object|null} citation
 * @param {Array} references Added references from the mark
 * @returns {Object|null}
 */
export function findAddedReference(citation, references) {
    if (!citation || !Array.isArray(references) || references.length === 0) {
        return null;
    }

    const localId = citation.localId || generateLocalId(citation);
    if (localId) {
        const byId = references.find(
            (r) => r.localId === localId || r.firestoreId === localId
        );
        if (byId) return byId;
    }

    if (citation.docId == null) return null;

    const citationIndex = citation.index ?? citation.segmentIndex ?? null;
    return references.find((r) => {
        if (r.docId == null || String(r.docId) !== String(citation.docId)) return false;
        const refIndex = r.index ?? r.segmentIndex ?? null;
        return refIndex != null && citationIndex != null && refIndex === citationIndex;
    }) ?? null;
}

/**
 * @param {Object|null} citation
 * @param {Array} references Added references from the mark
 * @returns {boolean}
 */
export function isCitationAlreadyAdded(citation, references) {
    return findAddedReference(citation, references) != null;
}

